import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Button,
  TextField,
  Autocomplete,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { requestExecutiveApproval } from 'services/vendor-assessment-service';
import { getUsersNameEmailByCompany } from 'services/users-service';
import { useAuthTenant } from 'store/auth';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

/**
 * ExecutiveApprovalModal Component
 * Content for requesting executive approval for high-risk assessments.
 * Renders without its own Dialog — parent provides the Dialog wrapper.
 */
export const ExecutiveApprovalModal = ({ assessmentId, trustScore, onSuccess, onClose }) => {
  const { t } = useTranslation(['gap-analysis']);
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);
  const tenant = useSelector(useAuthTenant);

  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [justification, setJustification] = useState('');

  // Fetch executives (company users)
  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['executives', tenant?.slug],
    () => getUsersNameEmailByCompany(tenant?.slug),
    { enabled: !!tenant?.slug }
  );

  const executives = usersData?.data || [];

  const requestMutation = useMutation(
    async () => {
      const response = await requestExecutiveApproval(assessmentId, selectedExecutive._id, justification);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to request executive approval');
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessmentId]);
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.executiveApproval.swal.successTitle'),
          html: `<p>${t('gap-analysis:assessment.executiveApproval.swal.successHtml', { name: selectedExecutive.name })}</p>`,
          timer: 3000
        });
        setSelectedExecutive(null);
        setJustification('');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        if (onClose) onClose();
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.executiveApproval.swal.errorTitle'),
          text: error.message || error.response?.data?.error || t('gap-analysis:assessment.executiveApproval.swal.errorTitle')
        });
      }
    }
  );

  const handleSubmit = () => {
    if (!selectedExecutive) {
      SwalReact.fire({
        icon: 'warning',
        title: t('gap-analysis:assessment.executiveApproval.swal.noExecutiveTitle'),
        text: t('gap-analysis:assessment.executiveApproval.swal.noExecutiveText')
      });
      return;
    }

    if (!justification.trim()) {
      SwalReact.fire({
        icon: 'warning',
        title: t('gap-analysis:assessment.executiveApproval.swal.noJustificationTitle'),
        text: t('gap-analysis:assessment.executiveApproval.swal.noJustificationText')
      });
      return;
    }

    requestMutation.mutate();
  };

  return (
    <Box>
      {/* Trust Score Display */}
      {trustScore && (
        <Box display="flex" justifyContent="center" mb={3} mt={2}>
          <Typography variant="h3" fontWeight="bold" color={
            trustScore.percentage >= 90 ? 'success.main' :
            trustScore.percentage >= 70 ? 'warning.main' : 'error.main'
          }>
            {trustScore.percentage}%
          </Typography>
        </Box>
      )}

      {/* Risk Summary */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {t('gap-analysis:assessment.executiveApproval.riskWarning')}
        </Typography>
      </Alert>

      {/* Critical Deficiencies Summary */}
      {trustScore?.criticalDeficiencies && trustScore.criticalDeficiencies.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            {t('gap-analysis:assessment.executiveApproval.criticalDeficiencies', { count: trustScore.criticalDeficiencies.length })}
          </Typography>
          <List dense>
            {trustScore.criticalDeficiencies.slice(0, 3).map((deficiency, idx) => (
              <ListItem key={idx} sx={{ pl: 0 }}>
                <ListItemText
                  primary={deficiency.controlDescription}
                  secondary={t('gap-analysis:assessment.executiveApproval.gapLabel', { gap: deficiency.gap })}
                />
              </ListItem>
            ))}
            {trustScore.criticalDeficiencies.length > 3 && (
              <ListItem sx={{ pl: 0 }}>
                <ListItemText
                  secondary={t('gap-analysis:assessment.executiveApproval.moreDeficiencies', { count: trustScore.criticalDeficiencies.length - 3 })}
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}

      {/* Executive Selection */}
      {usersLoading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Autocomplete
          options={executives}
          getOptionLabel={(exec) => `${exec.name} (${exec.email})`}
          value={selectedExecutive}
          onChange={(e, value) => setSelectedExecutive(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('gap-analysis:assessment.executiveApproval.selectExecutive')}
              required
              helperText={t('gap-analysis:assessment.executiveApproval.selectExecutiveHelper')}
            />
          )}
          sx={{ mb: 2 }}
        />
      )}

      {/* Justification */}
      <TextField
        fullWidth
        multiline
        rows={4}
        label={t('gap-analysis:assessment.executiveApproval.justification')}
        required
        value={justification}
        onChange={(e) => setJustification(e.target.value)}
        placeholder={t('gap-analysis:assessment.executiveApproval.justificationPlaceholder')}
        helperText={t('gap-analysis:assessment.executiveApproval.justificationHelper')}
        sx={{ mb: 2 }}
      />

      {/* Actions */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={requestMutation.isLoading || !selectedExecutive || !justification.trim()}
        >
          {requestMutation.isLoading ? <CircularProgress size={24} /> : t('gap-analysis:assessment.executiveApproval.sendButton')}
        </Button>
      </Box>
    </Box>
  );
};
